/*
 * The marks every dialect of this editor shares. `parseDOM` is again the clipboard's door — the
 * styles Word, Google Docs and a browser page write for bold and italic are read here — and `toDOM`
 * is the editor's picture of them.
 *
 * **The order of the record is the order of the schema, and the schema's order is nesting order:**
 * the first mark declared is the outermost one when two of them cover the same characters. That is
 * a dialect's decision and not this file's — a message wants `*`…`*` around a link, a document
 * wants the link around the code span — so a dialect builds its own record from these entries in
 * the order it wants and this file returns them in none in particular.
 */

import type { AttributeSpec, DOMOutputSpec, MarkSpec } from "prosemirror-model"

export interface MarkOptions {
	/** What a code span may hold: `"_"` for nothing at all, `""` where a mark may sit inside one. */
	codeExcludes: string
	/** Attributes a link carries beyond `href`, `title` and `auto` — the markup it was written in. */
	linkAttrs?: Record<string, AttributeSpec>
	/** Whether a destination may stay a link when it arrives through the clipboard. */
	allowedHref: (dest: string) => boolean
}

/**
 * The font weights that are bold: from 600, where a browser's own editing starts calling text bold
 * (Google Docs and Word write 700). A 500 is "medium", a page's typography, and a web page's lead
 * paragraph is set in it. The clipboard reader applies the same rule to the style attribute it reads
 * (`html/parseHtml.ts`).
 */
export const BOLD_WEIGHT = /^(bold(er)?|[6-9]\d\d)$/

function attrString(dom: HTMLElement, name: string): string {
	return dom.getAttribute(name) ?? ""
}

export function baseMarks(options: MarkOptions) {
	return {
		em: {
			parseDOM: [
				{ tag: "i" },
				{ tag: "em" },
				{ style: "font-style=italic" },
				{ style: "font-style=normal", clearMark: (m) => m.type.name === "em" },
			],
			toDOM: (): DOMOutputSpec => ["em", 0],
		},

		strong: {
			parseDOM: [
				{ tag: "strong" },
				{ tag: "b", getAttrs: (dom: HTMLElement) => dom.style.fontWeight !== "normal" && null },
				{ style: "font-weight=400", clearMark: (m) => m.type.name === "strong" },
				{
					style: "font-weight",
					getAttrs: (value: string) => (BOLD_WEIGHT.test(value) ? null : false),
				},
			],
			toDOM: (): DOMOutputSpec => ["strong", 0],
		},

		strike: {
			parseDOM: [
				{ tag: "s" },
				{ tag: "del" },
				{ tag: "strike" },
				{ style: "text-decoration=line-through" },
				{ style: "text-decoration-line=line-through" },
			],
			toDOM: (): DOMOutputSpec => ["s", 0],
		},

		code: {
			excludes: options.codeExcludes,
			parseDOM: [{ tag: "code" }],
			toDOM: (): DOMOutputSpec => ["code", { class: "tb-codespan" }, 0],
		},

		link: {
			attrs: {
				href: { default: "", validate: "string" },
				title: { default: "", validate: "string" },
				auto: { default: false, validate: "boolean" },
				...options.linkAttrs,
			},
			// Typing on after a link is not part of the link.
			inclusive: false,
			parseDOM: [
				{
					tag: "a[href]",
					getAttrs: (dom: HTMLElement) => {
						const href = attrString(dom, "href")
						// A refused destination keeps its text and loses the anchor, which is what the
						// markdown of the same destination does.
						if (!options.allowedHref(href)) return false
						return { href, title: attrString(dom, "title"), auto: false }
					},
				},
			],
			toDOM: (mark): DOMOutputSpec => [
				"a",
				{ href: mark.attrs.href as string, title: (mark.attrs.title as string) || null },
				0,
			],
		},
	} satisfies Record<string, MarkSpec>
}
